// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardDataFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * Dashboard Data Fetcher.
 */
import fetch from 'share/fetch/Izenda.Fetch';
import LoadingManager from 'modules/Izenda.LoadingManager';
import { isNil, omit } from 'lodash/fp';
const ROOT_URL = 'dashboard/';

const fetchers = {
  /**
   * Get all categories
   * @param {String} saveType
   * @returns {promise}
   */
  loadAllCategories: (tenantId: string): any => {
    tenantId = isNil(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}categories/${tenantId}`).get();
  },

  /**
   * Load category for dashboard (lazy load)
   */
  loadLazyLoadCategories: (searchFilter: any): any => {
    return fetch(`${ROOT_URL}allowedSavingCategories`).post(searchFilter);
  },

  /**
   * Load subcategory for dashboard (lazy load)
   */
  loadLazyLoadSubcategories: (searchFilter: any): any => {
    return fetch(`${ROOT_URL}allowedSavingSubCategories`).post(searchFilter);
  },

  getSubCategories: (searchFilter: any): any => {
    return fetch(`${ROOT_URL}subcategories`);
  },

  /**
   * Search report Categories
   * @param  {Object} data [description]
   * @return {Promise} array of ReportCategories
   */
  searchCategories: (request: any): any => {
    return fetch(`${ROOT_URL}search`).post(request);
  },

  /**
   * Lazy search dashboard categories
   * @param  {Object} data
   * @return {Promise}
   */
  lazyLoadingSearchCategories: (request: any): any => {
    return fetch(`${ROOT_URL}search2`).post(request);
  },

  /**
   * validate category name
   * @param  {object} category
   * @returns {Promise}
   */
  validateCategoryName: (category: any): any => {
    return fetch(`${ROOT_URL}category`).post(category);
  },

  /**
   * Delete dashboard
   * @param  {string}  dashboardId dashboard id
   * @return {Promise}
   */
  deleteDashboard: (dashboardId: string): any => {
    return fetch(ROOT_URL + dashboardId).delete();
  },

  /**
   * Delete a ReportCategory by id
   * @param {string} reportCategoryId
   * @returns {promise}
   */
  deleteReportCategory: (reportCategoryId: string): any => {
    return fetch(`${ROOT_URL}category/${reportCategoryId}`).delete();
  },

  /**
   * Load dashboard subscription
   * @param  {string} tenantId
   * @param  {object} pageInfo
   * @param  {string} dashboardId
   * @param  {Boolean} isSubscribe - Subscribe or Scheduling
   */
  loadSubscription: (tenantId: string, pageInfo: any, dashboardId: string, isSubscribe: boolean): any => {
    pageInfo = pageInfo || {};

    const requestData = {
      dashboardId,
      isSubscription: isSubscribe,
      tenantId,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'name', descending: true }]
    };

    return fetch(`${ROOT_URL}loadSubscriptions`).post(requestData);
  },

  /**
   * Get email templates
   * @returns {promise}
   */
  fetchEmailTemplates: templateType => {
    return fetch(`${ROOT_URL}emailTemplates/${templateType}`).get();
  },

  /**
   * Validate subscription instance.
   * @param  {Object} instance
   * @param {Array} commonFilterFields
   * @returns {Promise}
   */
  validateSubscription: (instance, commonFilterFields) => {
    const requestInfo = {
      subscription: omit(['reportFilter'], instance),
      commonFilterFields
    };

    return fetch(`${ROOT_URL}subscription/validate`).post(requestInfo);
  },

  /**
   * Load list field for compare field dropdown list.
   * @param {Object} requestInfo
   * @returns {promise}
   */
  loadFilterDataSource: requestInfo => {
    return fetch(`${ROOT_URL}loadFilterComparisonFields`).post(requestInfo);
  },

  /**
   * Load filter field data
   * @param {Object} requestInfo
   * @returns {array}
   */
  loadFilterFieldData: requestInfo => {
    return fetch(`${ROOT_URL}loadFilterFieldData`).post(requestInfo);
  },

  /**
   * Load partial filter field data
   * @param {Object} requestInfo
   * @returns {array}
   */
  loadPartialFilterFieldData: requestInfo => {
    return fetch(`${ROOT_URL}loadPartialFilterFieldData`).post(requestInfo);
  },

  /**
   * Load filter field data as tree style.
   * @param {Object} requestInfo
   * @returns {array}
   */
  loadFilterFieldDataAsTree: requestInfo => {
    return fetch(`${ROOT_URL}loadFilterFieldDataAsTree`).post(requestInfo);
  },

  /**
   * Load partial filter field data as tree style.
   * @param {Object} requestInfo
   * @returns {array}
   */
  loadPartialFilterFieldDataAsTree: requestInfo => {
    return fetch(`${ROOT_URL}loadPartialFilterFieldDataAsTree`).post(requestInfo);
  },

  /**
   * Load all dashboard categories
   * @param  {Object} data
   * @return {Promise}
   */
  loadDashboardCategories: (data, includeHashCode) => {
    includeHashCode = isNil(includeHashCode) ? false : includeHashCode;
    return fetch(`${ROOT_URL}list?includeHashCode=${includeHashCode}`).post(data);
  },

  /**
   * Load lazy all dashboards
   * @param  {Object} data
   * @param  {Boolean} includeHashCode
   * @return {Promise}
   */
  lazyLoadingDashboards: (data, includeHashCode) => {
    includeHashCode = isNil(includeHashCode) ? false : includeHashCode;
    return fetch(`${ROOT_URL}list2?includeHashCode=${includeHashCode}`).post(data);
  },

  /**
   * Move dashboard to another category/ sub-categories
   * @param  {Object} data moved dashboad
   * @return {Promise}
   */
  moveDashboard: data => {
    return fetch(`${ROOT_URL}move`).post(data);
  },

  /**
   * Rename dashboad
   * @param  {Object} data rename dashboad
   * @return {Promise}
   */
  renameDashboard: data => {
    return fetch(`${ROOT_URL}rename`).post(data);
  },

  /**
   * Copy existing dashboad
   * @param  {Object} data copy dashboad
   * @return {Promise}
   */
  copyDashboard: data => {
    return fetch(`${ROOT_URL}copy`).post(data);
  },

  /**
   * Validate dashboard name
   * @param {Object} dashboard detail model
   * @returns {promise}
   */
  validateDashboardName: data => {
    return fetch(`${ROOT_URL}validateDashboardName`).post(data);
  },

  /**
   * Load dashboard
   * @param {String} dashboard params
   * @returns {Object}
   */
  loadDashboard: dashboardParams => {
    return fetch(`${ROOT_URL}loadDashboard`).post(dashboardParams);
  },

  /**
   * load common filter
   * @method loadCommonFilters
   * @param  {[type]}          dashboardDefinition [description]
   * @return {[type]}                              [description]
   */
  loadCommonFilters: dashboardDefinition => {
    return fetch(`${ROOT_URL}loadCommonFilters`).post(dashboardDefinition);
  },

  /**
   * load dashboardpart fielterDesription
   * @method loadFilterDescription
   * @param  {[type]}              request [description]
   * @return {[type]}                      [description]
   */
  loadFilterDescription: request => {
    return fetch(`${ROOT_URL}loadFilterDescription`).post(request);
  },

  /**
   * for saved dashboard
   * @method loadDashboardPartData
   * @param  {[type]}              request [description]
   * @return {[type]}                      [description]
   */
  loadDashboardPartData: request => {
    return fetch(`${ROOT_URL}loadDashboardPartData`).post(request);
  },

  /**
   * for editing dashboard
   * @method previewDashboardPartData
   * @param  {[type]}                 request [description]
   * @return {[type]}                         [description]
   */
  previewDashboardPartData: request => {
    return fetch(`${ROOT_URL}previewDashboardPartData`).post(request);
  },

  /**
   * Save dashboard
   * @param {Object} dashboard detail
   * @returns {Object}
   */
  saveDashboard: dashboardDetail => {
    return fetch(ROOT_URL).post(dashboardDetail);
  },

  /**
   * Save dashboard subscriptions
   * @param  {Object} data
   * @returns {Promise}
   */
  saveSubscriptions: data => {
    return fetch(`${ROOT_URL}subscriptions`).post(data);
  },

  /**
   * Fetch definition of dashboard part.
   * @param  {String} id
   */
  loadDashboardPartDefinition: id => {
    return fetch(`${ROOT_URL}dashboardPart/${id}`).get();
  },

  /**
   * Load accesses
   * @param  {string} reportId
   * @param  {Object} subscriptions
   * @param  {Object} pageInfo
   */
  loadAccesses: (dashboardId, data, pageInfo) => {
    pageInfo = pageInfo || {};

    const requestData = {
      dashboardId,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [{ key: 'all', descending: true }]
    };

    return fetch(`${ROOT_URL}loadAccesses`).post(requestData);
  },

  /**
   * Validationn dashboard accesses
   * @param  {object} requestData
   */
  validateAccesses: requestData => {
    return fetch(`${ROOT_URL}accesses/validate`).post(requestData);
  },

  /**
   * Validate dashboard
   * @param {Object} dashboard detail
   * @returns {Object}
   */
  validateDashboard: dashboardDetail => {
    return fetch(`${ROOT_URL}validate`).post(dashboardDetail);
  },

  /**
   * Get allowed saving categories
   * @param {String} tenant id
   * @returns {Object}
   */
  getAllowedSavingCategories: tenantId => {
    tenantId = isNil(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}allowedSavingCategories/${tenantId}`).get();
  },

  /**
   * update renderingTime
   * @param  {string} id
   * @param  {Number} renderingTime
   */
  updateRenderingTime: (dashboardId, renderingTime) => {
    const requestData = {
      id: dashboardId,
      renderingTime
    };

    return fetch(`${ROOT_URL}updateRenderingTime`).post(requestData);
  },

  /**
   * Get dashboard common filter fields
   * @param  {string} dashboardId
   * @return {Promise}
   */
  getCommonFilterById: dashboardId => {
    return fetch(`${ROOT_URL}commonFilters/${dashboardId}`).get();
  },

  /**
   * save draft data use for printing
   * @method saveDraftDataForPrint
   * @param  {JSON}              dashboardDefinition [description]
   * @return {Promise}                                  [description]
   */
  saveDraftDataForPrint: dashboardDefinition => {
    return fetch(`${ROOT_URL}draft`).post(dashboardDefinition);
  },

  /**
   * use draft id when save draft to get draft data
   * @method getDraftDataForPrint
   * @param  {string}              draftId [description]
   * @return {Promise}                                  [description]
   */
  getDraftDataForPrint: draftId => {
    return fetch(`${ROOT_URL}draft/${draftId}`).get();
  },

  /**
   * Get all categories
   * @param {Object} param
   * @returns {promise}
   */
  loadLazyLoadingCategories: param => {
    return fetch(`${ROOT_URL}categories/`).post(param);
  },

  /**
   * Validate Global Dashboard.
   */
  validateGlobalDashboard: dashboardDefinition => {
    return fetch(`${ROOT_URL}validateGlobalDashboard`).post(dashboardDefinition);
  },

  /**
   * Load related dashboard part
   */
  loadRelatedDashboardParts: id => {
    return fetch(`${ROOT_URL}loadRelatedDashboardParts/${id}`).get();
  }
};

LoadingManager.linkToLoadingProgress(fetchers, {
  deleteDashboard: {
    allowToCancel: false
  },
  deleteReportCategory: {
    allowToCancel: false
  },
  moveDashboard: {
    allowToCancel: false
  },
  renameDashboard: {
    allowToCancel: false
  },
  copyDashboard: {
    allowToCancel: false
  },
  saveDashboard: {
    allowToCancel: false
  },
  saveSubscriptions: {
    allowToCancel: false
  },
  lazyLoadingDashboards: {
    allowToCancel: false
  },
  loadLazyLoadingCategories: {
    allowToCancel: false
  }
});

export default fetchers;



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.DashboardDataFetcher.js